import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Chip,
  ChipProps,
  Grid,
  Link,
  Skeleton,
  Typography,
} from '@mui/material';
import { compact, find, isEmpty, join, last, map, reject, size, split, startCase } from 'lodash';
import pluralize from 'pluralize';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { IngestJob, JobType, TileGenerationJob } from 'interfaces/job';
import { useJobIngestDetails } from 'utils/queryHooks/jobs/useJobIngestDetails';
import { useJobs } from 'utils/queryHooks/jobs/useJobs';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { useNavigationToViewerPage } from 'utils/useNavigationToViewerPage';
import { JobDetailCard, JobDetailSection, JobDetailValue } from './JobDetailCard';
import { JobDetailsLink } from './JobDetailsLink';

const overallStatusLabels: Record<string, string> = {
  slide_record_present: 'Slide Record Found',
  completed_missing_slide_record: 'Ingest Complete, Slide Record Missing',
  ingest_in_progress: 'Ingest In Progress',
  tiles_ready: 'Tiles Ready',
  tiles_partially_ready: 'Tiles Partially Ready',
  ingested_tiles_missing: 'Ingest Complete, Tiles Missing',
  native_resolution_tiles_missing: 'Native Resolution Tiles Missing',
  native_resolution_tiles_partially_ready: 'Native Resolution Tiles Partially Ready',
  not_ingested: 'Not Ingested',
  unsupported_source_file: 'Unsupported Source File',
  unknown: 'Unknown',
};

export const getOverallStatusColor = (overallStatus: string): ChipProps['color'] => {
  if (
    overallStatus === 'completed_missing_slide_record' ||
    overallStatus === 'ingested_tiles_missing' ||
    overallStatus === 'native_resolution_tiles_missing'
  ) {
    return 'error';
  }

  if (overallStatus === 'slide_record_present' || overallStatus === 'tiles_ready') {
    return 'success';
  }

  if (
    overallStatus === 'tiles_partially_ready' ||
    overallStatus === 'native_resolution_tiles_partially_ready' ||
    overallStatus === 'unsupported_source_file'
  ) {
    return 'warning';
  }

  if (overallStatus === 'ingest_in_progress') {
    return 'info';
  }

  if (overallStatus.startsWith('latest_')) {
    const latestStatus = overallStatus.replace('latest_', '');
    if (latestStatus.includes('failed') || latestStatus.includes('cancelled')) {
      return 'error';
    }

    return 'default';
  }

  if (overallStatus === 'unknown') {
    return 'warning';
  }

  if (overallStatus === 'not_ingested') {
    return 'default';
  }

  if (overallStatus.includes('failed')) {
    return 'error';
  }

  return 'default';
};

export const formatOverallStatus = (value: string) => {
  if (value.startsWith('latest_')) {
    return `Ingest ${startCase(value.replace('latest_', ''))}`;
  }

  return overallStatusLabels[value] || startCase(value);
};

/**
 * Returns the trailing file name from an ingest source URI or path-like string.
 *
 * The helper removes only the first transport prefix separator so it works with
 * both ingest-style remote paths and standard URI-style values.
 *
 * Examples:
 * - `S3_slides:test-bucket/folder/source-slide.tif` -> `source-slide.tif`
 * - `s3://test-bucket/folder/scan:01/source-slide.tif` -> `source-slide.tif`
 * - `undefined` -> `undefined`
 */
const extractSourceFileName = (sourceSlideUri?: string) => {
  if (!sourceSlideUri) {
    return undefined;
  }

  const separatorIndex = sourceSlideUri.indexOf(':');
  const sourcePath = separatorIndex >= 0 ? sourceSlideUri.slice(separatorIndex + 1) : sourceSlideUri;
  return last(compact(split(sourcePath, '/')));
};

const getJobSourceSlideUri = (job: Partial<IngestJob>) => job.params?.source_slide_uri;

const getJobLabId = (job: Partial<IngestJob>) => job.params?.lab_id;

const getJobSlideId = (job: Partial<IngestJob>) => job.results?.slide_id;

export const IngestJobDetailsSection: React.FC<{
  jobId: string;
  job: Partial<IngestJob>;
}> = ({ jobId, job }) => {
  const { data: ingestDetails, isLoading, isError } = useJobIngestDetails(jobId);
  const relatedSlideIds = ingestDetails?.slideIds || [];
  const hasRelatedSlideIds = !isEmpty(relatedSlideIds);
  const { data: relatedTileJobsData, isLoading: isLoadingRelatedTileJobs } = useJobs<TileGenerationJob>({
    enabled: !isError && hasRelatedSlideIds,
    page: 1,
    pageSize: Math.max(size(relatedSlideIds), 1),
    additionalFilters: {
      types: [JobType.TileGeneration],
      slideIds: relatedSlideIds,
    },
    studyId: job.studyId,
    keepPreviousData: false,
  });
  const { getUrlToSlidePage } = useNavigationToViewerPage();
  const { labId: currentLabId } = useCurrentLabId();

  const sourceFileName = extractSourceFileName(getJobSourceSlideUri(job));
  const currentJobSlideId = getJobSlideId(job);
  const resolvedLabId = getJobLabId(job) || ingestDetails?.labId || currentLabId;
  const currentJobSlide = find(ingestDetails?.slides, (slide) => slide.slideId === currentJobSlideId);
  const additionalSlides = reject(ingestDetails?.slides || [], (slide) => slide.slideId === currentJobSlideId);
  const hasMatchedSlides = size(additionalSlides) > 0;
  const relatedTileJobs = relatedTileJobsData?.jobs || [];
  const hasMultipleAttempts = (ingestDetails?.jobAttemptCount || 0) > 1;
  const primarySlideUrl =
    currentJobSlideId && resolvedLabId
      ? getUrlToSlidePage({ slideId: currentJobSlideId, labId: resolvedLabId })
      : undefined;
  const primarySlideSummary = currentJobSlide ? join(compact([currentJobSlide.slideFormat]), ' · ') : undefined;

  return (
    <Grid item>
      <Accordion defaultExpanded slotProps={{ transition: { unmountOnExit: true } }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="ingest-details-content"
          id="ingest-details-header"
        >
          <Grid container alignItems="center" spacing={1} wrap="nowrap">
            <Grid item xs>
              <Typography variant="h4">Ingest Details</Typography>
            </Grid>
            {ingestDetails && !isError && (
              <Grid item>
                <Chip
                  label={formatOverallStatus(ingestDetails.overallStatus)}
                  color={getOverallStatusColor(ingestDetails.overallStatus)}
                  variant="outlined"
                  size="small"
                />
              </Grid>
            )}
          </Grid>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container direction="column" spacing={2}>
            <Grid item>
              <JobDetailCard>
                <JobDetailSection label="Processed slide details">
                  {currentJobSlideId ? (
                    <Grid container direction="column">
                      {primarySlideUrl ? (
                        <Link
                          to={primarySlideUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          component={RouterLink}
                          underline="hover"
                        >
                          {currentJobSlideId}
                        </Link>
                      ) : (
                        <Typography variant="body1">{currentJobSlideId}</Typography>
                      )}
                      {!isEmpty(primarySlideSummary) && (
                        <Typography variant="caption" color="text.secondary">
                          {primarySlideSummary}
                        </Typography>
                      )}
                      {hasMatchedSlides && (
                        <Typography variant="caption" color="text.secondary">
                          +{size(additionalSlides)} matched slide {pluralize('record', size(additionalSlides))}
                        </Typography>
                      )}
                    </Grid>
                  ) : null}
                </JobDetailSection>

                {(hasMultipleAttempts || ingestDetails?.reprocessingDetected) && (
                  <Grid item>
                    <Typography variant="caption" color="text.secondary">
                      {hasMultipleAttempts
                        ? `${ingestDetails?.jobAttemptCount} ingest attempts found for this source.`
                        : 'Reprocessing evidence detected for this source.'}
                    </Typography>
                  </Grid>
                )}

                {isLoading && !ingestDetails ? <Skeleton variant="rectangular" height={140} /> : null}

                {sourceFileName && (
                  <JobDetailSection label="Source file">
                    <JobDetailValue variant="h5">{sourceFileName || '---'}</JobDetailValue>
                  </JobDetailSection>
                )}

                {!isLoading && !isError && ingestDetails && hasMatchedSlides && (
                  <Grid item container direction="column" spacing={1}>
                    <Grid item>
                      <Typography variant="body2" color="text.secondary">
                        {currentJobSlideId
                          ? 'Other slide records matched to this source file'
                          : 'Slide records matched to this source file'}
                      </Typography>
                    </Grid>
                    {map(additionalSlides, (slide) => {
                      const slideUrl = resolvedLabId
                        ? getUrlToSlidePage({ slideId: slide.slideId, labId: resolvedLabId })
                        : undefined;
                      const slideSummary = join(compact([slide.slideFormat]), ' · ');

                      return (
                        <Grid item key={slide.slideId} container direction="column">
                          {slideUrl ? (
                            <Link
                              to={slideUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              component={RouterLink}
                              underline="hover"
                            >
                              {slide.slideId}
                            </Link>
                          ) : (
                            <Typography variant="body1">{slide.slideId}</Typography>
                          )}
                          {!isEmpty(slideSummary) && (
                            <Typography variant="caption" color="text.secondary">
                              {slideSummary}
                            </Typography>
                          )}
                        </Grid>
                      );
                    })}
                  </Grid>
                )}

                {!isLoading && !isError && ingestDetails && isLoadingRelatedTileJobs && (
                  <Grid item>
                    <Skeleton variant="rectangular" height={70} />
                  </Grid>
                )}

                {!isLoading && !isError && ingestDetails && !isEmpty(relatedTileJobs) && (
                  <Grid item container direction="column" spacing={1}>
                    <Grid item>
                      <Typography variant="body2" color="text.secondary">
                        Matched tile generation {pluralize('job', size(relatedTileJobs))}
                      </Typography>
                    </Grid>
                    {map(relatedTileJobs, (tileJob) => (
                      <Grid item key={tileJob.id} container direction="column">
                        <Typography variant="body1">{tileJob.name || tileJob.id}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {tileJob.id ? <JobDetailsLink jobId={tileJob.id}>{tileJob.id}</JobDetailsLink> : null}
                          {tileJob.id && tileJob.status ? ' · ' : null}
                          {tileJob.status}
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>
                )}

                {!isLoading && !isError && ingestDetails && !isEmpty(ingestDetails.warnings) && (
                  <Grid item>
                    {map(ingestDetails.warnings, (warning) => (
                      <Alert severity="warning" key={warning}>
                        {warning}
                      </Alert>
                    ))}
                  </Grid>
                )}
              </JobDetailCard>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Grid>
  );
};
